// Song page (contract D.4.2, B.5). App.js hosts it in a
// `panel panel-right panel-wide`; this renders the panel head and body.
//
// Plays, debut, last played, the current gap (active bands only), a year
// strip, the 10 longest versions (playable), bust-outs, "Went into" and
// "Came out of" bars, other bands that play it, Song Radio (In order /
// Longest first / Shuffle, with "Let it land") and "Add to Set Search".
//
// Props: { band, songKey, onClose, onPlaySegments, onOpenShow, onOpenSong, onAddToSearch }

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api';
import { bandByKey } from '../bands';
import { makeSource } from '../queue';
import { newSeed } from './radioSource';
import { IconClose, IconPlay, IconOpen, IconPlusCircle, IconRadio, IconNote } from '../icons';
import YearStrip from './YearStrip';
import { resolveBand, fmtDate, fmtSecs, fmtCount, plural, yearOf, dateKeyFor, arrowsRare, ORDER_LABELS, ORDER_HINTS } from './vibeUtil';
import { partBadge, partBadges } from '../showPart';
import { FromTapeBadge, isFillBand } from '../fromTape';
import './vibe.css';

const BAR_MAX = 8;

// band meta, fetched once per band per page load (years range, radio flag)
const metaCache = new Map();
const loadMeta = (bandKey) => {
  if (!metaCache.has(bandKey)) {
    const p = api.fetchMeta(bandKey).catch((err) => {
      metaCache.delete(bandKey);
      throw err;
    });
    metaCache.set(bandKey, p);
  }
  return metaCache.get(bandKey);
};

// In a band that mixes setlist shows with shows matched from tape titles
// (fillBand), the gap counts shows with a setlist only; a play at a later
// tape-title show still resets it (coverage SPEC 5.5, 7.5).
const gapText = (gap, fillBand, last) => {
  const n = gap && typeof gap.shows === 'number' ? gap.shows : null;
  if (n == null) return null;
  const sl = fillBand ? ' with a setlist' : '';
  if (n === 0) {
    if (last && last.fromTape) return 'Played since the most recent show with a setlist';
    return 'Played at the most recent show' + sl;
  }
  if (n === 1) return 'Not played at the last show' + sl;
  return 'Not played in the last ' + fmtCount(n) + ' shows' + sl;
};

const Bars = ({ rows, verb, onOpen }) => {
  const list = rows.slice(0, BAR_MAX);
  const max = Math.max(1, ...list.map((r) => r.n || 0));
  return (
    <div className="vb-bars">
      {list.map((r) => (
        <button
          key={r.key}
          type="button"
          className="vb-bar"
          title={verb + ' ' + r.name + ' ' + plural(r.n, 'time') + '. Open song page.'}
          aria-label={verb + ' ' + r.name + ', ' + plural(r.n, 'time') + '. Open song page.'}
          onClick={() => onOpen(r.key)}
        >
          <span className="vb-bar-name">{r.name}</span>
          <span className="vb-bar-track" aria-hidden="true">
            <span className="vb-bar-fill" style={{ width: Math.round(((r.n || 0) / max) * 100) + '%', display: 'block' }} />
          </span>
          <span className="vb-bar-n mono" aria-hidden="true">
            {fmtCount(r.n)}
          </span>
        </button>
      ))}
    </div>
  );
};

const PlayBtn = ({ label, busy, busyKey, onClick }) => (
  <button type="button" className="icon-btn vb-icon-sm" title="Play" aria-label={'Play ' + label} onClick={onClick} aria-disabled={!!busy} aria-busy={busy === busyKey}>
    {busy === busyKey ? <span className="spinner vb-spin" aria-hidden="true" /> : <IconPlay size={13} />}
  </button>
);

const OpenBtn = ({ label, onClick }) => (
  <button type="button" className="icon-btn vb-icon-sm" title="Open show" aria-label={'Open show ' + label} onClick={onClick}>
    <IconOpen size={14} />
  </button>
);

// Optional (additive): claimPlayIntent() from App; its token goes to
// onPlaySegments so a late answer never overrides a newer pick.
export default function SongPage({ band, songKey, onClose, onPlaySegments, onOpenShow, onOpenSong, onAddToSearch, claimPlayIntent }) {
  const b = resolveBand(band);
  const bandKey = b ? b.key : null;
  const [st, setSt] = useState({ status: 'loading', data: null, error: null });
  const [meta, setMeta] = useState(null);
  const [land, setLand] = useState(false);
  const [busy, setBusy] = useState(null); // key of the play or radio request in flight
  const [retry, setRetry] = useState(0);
  const bodyRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    []
  );

  useEffect(() => {
    if (!bandKey || !songKey) return undefined;
    let live = true;
    setSt({ status: 'loading', data: null, error: null });
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    api
      .fetchSong(bandKey, songKey)
      .then((data) => live && setSt({ status: 'ready', data, error: null }))
      .catch((err) =>
        live &&
        setSt({
          status: 'error',
          data: null,
          error:
            err && err.status === 404
              ? 'This song is not in the ' + ((bandByKey(bandKey) || {}).name || 'band') + ' catalog.'
              : (err && err.userMessage) || 'Could not load details for this song.',
        })
      );
    return () => {
      live = false;
    };
  }, [bandKey, songKey, retry]);

  useEffect(() => {
    if (!bandKey) return undefined;
    let live = true;
    setMeta(null);
    loadMeta(bandKey)
      .then((m) => live && setMeta(m))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [bandKey]);

  if (!b) return null;

  const d = st.status === 'ready' ? st.data : null;
  const name = d ? d.name : '';
  const flags = (d && d.flags) || {};
  const rare = meta ? arrowsRare(meta) : false;
  const useLand = land && !rare;
  const radioOn = !(meta && meta.features && meta.features.radio && meta.features.radio.song === false);

  const toastErr = (err, fallback) => toast((err && err.userMessage) || fallback, { type: toast.TYPE.ERROR });

  const openShowAt = (row, bandKeyOverride) => {
    const bk = bandKeyOverride || b.key;
    onOpenShow({
      bandKey: bk,
      iso: row.iso,
      dateKey: row.dateKey || dateKeyFor(bk, row.iso),
      sid: row.sid || null,
      tapeId: row.tapeId || null,
    });
  };

  const playPerf = (bk, perfId, title, busyKey) => {
    if (!perfId || busy) return;
    setBusy(busyKey);
    const intent = claimPlayIntent ? claimPlayIntent() : undefined;
    api
      .fetchSegment(bk, perfId, useLand)
      .then((seg) => {
        if (!seg || !Array.isArray(seg.tracks) || !seg.tracks.length) {
          toast('This version is not playable right now.', { type: toast.TYPE.INFO });
          return;
        }
        onPlaySegments([seg], makeSource('show', bandByKey(bk) || b), { title, intent });
      })
      .catch((err) => toastErr(err, 'Could not load details for this song.'))
      .finally(() => aliveRef.current && setBusy(null));
  };

  const playRadio = (order) => {
    if (busy || !d) return;
    const params = { mode: 'song', song: d.key || songKey, order, land: useLand, seed: newSeed() };
    setBusy('radio:' + order);
    const intent = claimPlayIntent ? claimPlayIntent() : undefined;
    api
      .radio(b, params, null)
      .then((res) => {
        const segments = (res && res.segments) || [];
        if (!segments.length) {
          toast('No playable versions of ' + name + ' yet.', { type: toast.TYPE.INFO });
          return;
        }
        const cursor = (res && res.cursor) || null;
        onPlaySegments(segments, makeSource('song', b, { params, cursor, exhausted: !!(res && res.exhausted) || !cursor }), {
          title: (res.station && res.station.label) || name + ', ' + ORDER_LABELS[order],
          intent,
        });
      })
      .catch((err) => toastErr(err, 'The radio could not load more songs.'))
      .finally(() => aliveRef.current && setBusy(null));
  };

  const onKeyDown = (e) => {
    if ((e.key === 'Escape' || e.key === 'Esc') && !e.defaultPrevented) onClose();
  };

  const years = meta && Array.isArray(meta.years) ? meta.years : [yearOf(d && d.first && d.first.iso), yearOf(d && d.last && d.last.iso)];
  const longest = d && Array.isArray(d.longest) ? d.longest.slice(0, 10) : [];
  const bustouts = d && flags.bustoutsReliable !== false && Array.isArray(d.bustouts) ? d.bustouts.slice(0, 10) : [];
  const into = d && Array.isArray(d.into) ? d.into.filter((r) => r && r.key && r.name) : [];
  const from = d && Array.isArray(d.from) ? d.from.filter((r) => r && r.key && r.name) : [];
  const others = d && Array.isArray(d.otherBands) ? d.otherBands.filter((o) => o && bandByKey(o.bandKey)) : [];
  const fillBand = isFillBand(meta);
  const gap = d && d.currentGap && d.currentGap.bandActive ? gapText(d.currentGap, fillBand, d.last) : null;
  const tracksPlays = d && d.playsBySource && typeof d.playsBySource.tracks === 'number' ? d.playsBySource.tracks : null;

  const longestParts = partBadges(longest);
  const bustoutParts = partBadges(bustouts);
  const partEl = (pb) =>
    pb ? (
      <span className="badge vb-part" title={pb.title}>
        {pb.text}
      </span>
    ) : null;

  const tapeEl = (row) => (fillBand && row && row.fromTape ? <FromTapeBadge row={row} className="vb-part vb-fromtape" /> : null);

  const showTile = (label, row) =>
    row && row.iso ? (
      <button type="button" className="vb-stat" onClick={() => openShowAt(row)} title="Open show">
        <div className="vb-stat-label">{label}</div>
        <div className="vb-stat-value">
          {fmtDate(row.iso)}
          {partEl(partBadge(row))}
          {tapeEl(row)}
        </div>
        <div className="vb-stat-sub">{[row.venue, row.location].filter(Boolean).join(', ') || (row.fromTape ? 'Matched from tape titles' : '')}</div>
      </button>
    ) : null;

  return (
    <div className="vb-song vb-root" onKeyDown={onKeyDown}>
      <div className="panel-head">
        <div style={{ minWidth: 0 }}>
          <div className="panel-title">{d ? name : st.status === 'error' ? 'Song' : <span className="skel skel-line w60" style={{ display: 'block', width: 180, height: 18 }} />}</div>
          <div className="panel-sub">
            {b.name}
            {d && typeof d.plays === 'number' ? ' · ' + plural(d.plays, 'play') : ''}
          </div>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} title="Close" aria-label="Close">
          <IconClose size={18} />
        </button>
      </div>

      <div className="panel-body vb-song-body" ref={bodyRef} aria-busy={st.status === 'loading'}>
        {st.status === 'loading' && (
          <div aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <div className="skel-row" key={i}>
                <div className="skel skel-line w60" />
                <div className="skel skel-line w90" />
                <div className="skel skel-line w40" />
              </div>
            ))}
          </div>
        )}

        {st.status === 'error' && (
          <div className="vb-empty">
            <div>{st.error}</div>
            <button type="button" className="vb-btn" onClick={() => setRetry((n) => n + 1)}>
              Try again
            </button>
          </div>
        )}

        {d && (
          <>
            <div className="vb-stats">
              <div className="vb-stat">
                <div className="vb-stat-label">Plays</div>
                <div className="vb-stat-value">{fmtCount(d.plays)}</div>
                {typeof d.tapePlays === 'number' && <div className="vb-stat-sub">{fmtCount(d.tapePlays)} on tape</div>}
                {tracksPlays > 0 && <div className="vb-stat-sub">{fmtCount(tracksPlays)} from tape titles</div>}
              </div>
              {showTile('Debut', d.first)}
              {showTile('Earliest on tape', d.firstOnTape)}
              {showTile('Last played', d.last)}
              {gap && (
                <div className="vb-stat">
                  <div className="vb-stat-label">Current gap</div>
                  <div className="vb-stat-value" style={{ fontSize: 13 }}>
                    {gap}
                  </div>
                </div>
              )}
            </div>

            <div className="vb-actions" style={{ marginTop: 12 }}>
              <button type="button" className="vb-btn" onClick={() => onAddToSearch({ key: d.key || songKey, name })}>
                <IconPlusCircle size={14} /> Add to Set Search
              </button>
            </div>

            {radioOn && (
              <>
                <div className="vb-label">Song Radio</div>
                <div className="vb-radio-btns" role="group" aria-label="Song Radio">
                  {Object.keys(ORDER_LABELS).map((k) => (
                    <button key={k} type="button" className="vb-btn" title={ORDER_HINTS[k]} onClick={() => playRadio(k)} aria-disabled={!!busy} aria-busy={busy === 'radio:' + k}>
                      {busy === 'radio:' + k ? <span className="spinner vb-spin" aria-hidden="true" /> : <IconRadio size={14} />}
                      {ORDER_LABELS[k]}
                    </button>
                  ))}
                </div>
              </>
            )}
            {!rare && (
              <button
                type="button"
                role="switch"
                aria-checked={land}
                className="vb-switch"
                onClick={() => setLand(!land)}
                title="Keep playing to the end of each run of songs"
              >
                <span className="vb-switch-track" aria-hidden="true" />
                Let it land
                <span className="dim">keep playing to the end of each run</span>
              </button>
            )}

            <div className="vb-label">Plays by year</div>
            <YearStrip playsByYear={d.playsByYear} years={years} />

            {flags.lengthsReliable !== false && longest.length > 0 && (
              <>
                <div className="vb-label">Longest versions</div>
                {typeof d.medianSecs === 'number' && d.medianSecs > 0 && (
                  <div className="vb-lenline">
                    Usually runs {fmtSecs(d.medianSecs)}
                    {typeof d.p90Secs === 'number' && d.p90Secs > 0 ? '. 1 in 10 versions runs past ' + fmtSecs(d.p90Secs) : ''}
                    {typeof d.versionsWithSecs === 'number'
                      ? ' (' + plural(d.versionsWithSecs, 'timed version') + (fillBand ? ' from shows with a setlist' : '') + ').'
                      : '.'}
                  </div>
                )}
                <div className="vb-list">
                  {longest.map((v, i) => {
                    const where = [v.venue, v.location].filter(Boolean).join(', ') || (v.fromTape ? 'Venue unknown' : '');
                    const label = name + ', ' + fmtDate(v.iso);
                    return (
                      <div className="vb-vrow" key={v.perfId || i}>
                        <span className="vb-vrow-rank mono">{i + 1}</span>
                        <div className="row-main">
                          <div className="row-title">
                            {fmtDate(v.iso)}
                            {partEl(longestParts[i])}
                            {tapeEl(v)}
                          </div>
                          {where && <div className="row-sub">{where}</div>}
                        </div>
                        <span className="badge mono">{v.length || fmtSecs(v.secs)}</span>
                        <PlayBtn busy={busy} label={label} busyKey={'perf:' + v.perfId} onClick={() => playPerf(b.key, v.perfId, label, 'perf:' + v.perfId)} />
                        <OpenBtn label={fmtDate(v.iso)} onClick={() => openShowAt(v)} />
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {bustouts.length > 0 && (
              <>
                <div className="vb-label">Bust-outs</div>
                <div className="vb-list">
                  {bustouts.map((x, i) => {
                    const label = name + ', ' + fmtDate(x.iso);
                    return (
                      <div className="vb-vrow" key={x.perfId || i}>
                        <div className="row-main">
                          <div className="row-title">
                            {fmtDate(x.iso)}
                            {partEl(bustoutParts[i])}
                          </div>
                          <div className="row-sub">
                            {typeof x.gap === 'number' ? 'First time in ' + fmtCount(x.gap) + ' shows' : 'Back after a long break'}
                            {x.venue ? ', ' + x.venue : ''}
                          </div>
                        </div>
                        {x.perfId && <PlayBtn busy={busy} label={label} busyKey={'perf:' + x.perfId} onClick={() => playPerf(b.key, x.perfId, label, 'perf:' + x.perfId)} />}
                        <OpenBtn label={fmtDate(x.iso)} onClick={() => openShowAt(x)} />
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {into.length > 0 && (
              <>
                <div className="vb-label">Went into</div>
                <Bars rows={into} verb="Went into" onOpen={(k) => onOpenSong(k)} />
              </>
            )}
            {from.length > 0 && (
              <>
                <div className="vb-label">Came out of</div>
                <Bars rows={from} verb="Came out of" onOpen={(k) => onOpenSong(k)} />
              </>
            )}

            {others.length > 0 && (
              <>
                <div className="vb-label">Other bands</div>
                <div className="vb-list">
                  {others.map((o) => {
                    const ob = bandByKey(o.bandKey);
                    const span = [yearOf(o.first), yearOf(o.last)].filter(Boolean);
                    const spanText = span.length === 2 && span[0] !== span[1] ? span[0] + '-' + span[1] : span.length ? String(span[0]) : '';
                    const label = (o.name || name) + ' by ' + ob.name;
                    return (
                      <div className="vb-vrow" key={o.bandKey + ':' + o.songKey}>
                        <div className="row-main">
                          <div className="row-title">{ob.name}</div>
                          <div className="row-sub">
                            {typeof o.plays === 'number' ? plural(o.plays, 'play') : ''}
                            {spanText ? (typeof o.plays === 'number' ? ', ' : '') + spanText : ''}
                          </div>
                        </div>
                        {o.topPerfId && (
                          <PlayBtn busy={busy} label={label} busyKey={'xb:' + o.bandKey} onClick={() => playPerf(o.bandKey, o.topPerfId, label, 'xb:' + o.bandKey)} />
                        )}
                        {o.songKey && (
                          <button
                            type="button"
                            className="icon-btn vb-icon-sm"
                            title="Open song page"
                            aria-label={'Open song page: ' + label}
                            onClick={() => onOpenSong(o.songKey, o.bandKey)}
                          >
                            <IconNote size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
